import React, { useState } from 'react';
import { Avatar } from '../ui/Avatar';
import { BalanceRing } from '../dashboard/BalanceRing';
import { AddUserModal } from './AddUserModal';
import { UserPlus, MoreHorizontal, X } from 'lucide-react';

const PRIMARY = 'var(--accent)';

export function EmployeeCard({ employee, onSelect, remainingLabel, pendingLabel, hideDept = false }) {
  const used      = employee.used ?? 0;
  const balance   = employee.balance ?? 0;
  const usedPct   = balance > 0 ? Math.round((used / balance) * 100) : 0;

  // Use computed label if provided, otherwise fall back to raw numbers
  const displayRemaining = remainingLabel ?? `${balance - used}d`;
  const barColor = usedPct > 80 ? 'var(--bad-text)' : usedPct > 60 ? '#F5A300' : 'var(--accent)';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(employee)}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(employee); } }}
      style={{
        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14,
        padding: '14px 16px', cursor: 'pointer', outline: 'none',
        transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--field-border)'; e.currentTarget.style.boxShadow = 'var(--menu-shadow)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
      onFocus={e => { e.currentTarget.style.boxShadow = '0 0 0 3px var(--tint-strong)'; }}
      onBlur={e => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Avatar employee={employee} size="md" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{employee.name}</p>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{employee.role}</p>
        </div>
        <BalanceRing used={employee.used} balance={employee.balance} size={44} />
      </div>

      {((!hideDept && employee.dept) || employee.pending > 0) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
          {!hideDept && employee.dept && (
            <span style={{ fontSize: 11.5, fontWeight: 600, padding: '2px 9px', borderRadius: 10, background: 'var(--tint)', color: 'var(--accent)' }}>
              {employee.dept}
            </span>
          )}
          {employee.pending > 0 && (
            <span style={{ fontSize: 11.5, fontWeight: 600, padding: '2px 9px', borderRadius: 10, background: 'var(--warn-bg-soft)', color: 'var(--warn-text)', border: '1px solid var(--warn-border)' }}>
              {employee.pending} pending
            </span>
          )}
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6, fontSize: 11.5, color: 'var(--text-muted)' }}>
          <span>{used}d used</span>
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <span style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 12.5 }}>{displayRemaining}</span>
            <span>left</span>
            {pendingLabel && (
              <span style={{ color: 'var(--warn-text)', fontWeight: 600 }}>· {pendingLabel} pending</span>
            )}
          </span>
        </div>
        <div style={{ height: 5, borderRadius: 3, background: 'var(--border-soft)', overflow: 'hidden' }}>
          <div style={{ width: `${Math.min(100, usedPct)}%`, height: '100%', borderRadius: 3, background: barColor, transition: 'width 0.5s' }} />
        </div>
      </div>
    </div>
  );
}

export function MembersList({ employees, onSelect, onUserCreated, departments = [], currentUserName, companyName, allowancesMap = {}, schedulesMap = {}, leaveRequests = [], leaveColors = {}, currentUser }) {
  const [selected, setSelected] = React.useState([]);
  const [showAddUser, setShowAddUser] = useState(false);

  // Import calcAllowance inline to avoid circular deps — or import from util
  // We receive the computed maps as props and call calcAllowance per row
  const { calcAllowance } = require('../../utils/calcAllowance');

  const isPrivilegedUser = ['admin', 'super_admin', 'manager'].includes(currentUser?.role);

  const toggleAll = (checked) =>
    setSelected(checked ? employees.map(e => e.id) : []);

  const toggleOne = (id) =>
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const allChecked  = selected.length === employees.length && employees.length > 0;
  const someChecked = selected.length > 0 && !allChecked;

  return (
    <>
      <style>{`
        .ml-panel { background: var(--surface); border: 1px solid var(--border); border-radius: 18px; overflow: hidden; box-shadow: 0 1px 2px rgba(28,26,20,0.03); }
        .ml-row {
          display: grid; align-items: center; column-gap: 16px; padding: 0 20px;
          grid-template-columns: 20px minmax(180px, 1.3fr) minmax(180px, 1.4fr) minmax(130px, 1fr) minmax(150px, 1fr) 32px;
        }
        .ml-head { height: 44px; background: var(--surface-2); border-bottom: 1px solid var(--border-soft); }
        .ml-head span { font-size: 11px; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.06em; white-space: nowrap; }
        .ml-body { min-height: 62px; border-bottom: 1px solid var(--border-soft); cursor: pointer; transition: background 0.12s; }
        .ml-body:last-of-type { border-bottom: none; }
        .ml-body:hover { background: var(--surface-2); }
        .ml-body:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }
        .ml-body[data-selected="true"] { background: var(--surface-2); }
        .ml-check { width: 16px; height: 16px; cursor: pointer; accent-color: var(--accent); margin: 0; }
        .ml-cell { min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ml-more {
          width: 32px; height: 32px; display: inline-flex; align-items: center; justify-content: center;
          border: 1px solid transparent; border-radius: 9px; background: transparent; color: var(--text-muted); cursor: pointer;
          opacity: 0; transition: opacity 0.12s, background 0.12s, border-color 0.12s;
        }
        .ml-body:hover .ml-more, .ml-more:focus-visible { opacity: 1; }
        .ml-more:hover { background: var(--surface); border-color: var(--border); color: var(--accent); }
        @media (hover: none) { .ml-more { opacity: 1; } }
        .ml-add {
          height: 34px; padding: 0 13px; display: inline-flex; align-items: center; gap: 6px;
          border: 1px solid var(--border); border-radius: 9px; background: var(--surface); color: var(--accent);
          font-size: 12.5px; font-weight: 600; white-space: nowrap; cursor: pointer; transition: background 0.15s, border-color 0.15s;
        }
        .ml-add:hover { background: var(--tint); border-color: var(--field-border); }

        @media (max-width: 960px) {
          .ml-row { grid-template-columns: 20px minmax(160px, 1.4fr) minmax(120px, 1fr) minmax(130px, 1fr) 32px; }
          .ml-col-email { display: none; }
        }
        @media (max-width: 640px) {
          .ml-row { grid-template-columns: 20px minmax(0, 1fr) auto 32px; column-gap: 12px; padding: 0 14px; }
          .ml-col-dept { display: none; }
        }
      `}</style>

      <div className="ml-panel">
        {/* Title bar: count / selection, plus Add user */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 20px', borderBottom: '1px solid var(--border-soft)' }}>
          {selected.length > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, color: 'var(--text)' }}>
              <span style={{ fontWeight: 700 }}>{selected.length} selected</span>
              <button onClick={() => setSelected([])} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'none', padding: 0, color: 'var(--accent)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                <X size={13} /> Clear
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>People</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 8px', borderRadius: 10, background: 'var(--border)', color: 'var(--text-2)', fontSize: 11.5, fontWeight: 600 }}>{employees.length}</span>
            </div>
          )}
          <button onClick={() => setShowAddUser(true)} className="ml-add">
            <UserPlus size={14} />
            Add user
          </button>
        </div>

        {employees.length > 0 && (
          <div className="ml-row ml-head">
            <input
              type="checkbox"
              className="ml-check"
              checked={allChecked}
              ref={el => { if (el) el.indeterminate = someChecked; }}
              onChange={e => toggleAll(e.target.checked)}
              aria-label="Select everyone"
            />
            <span>Name</span>
            <span className="ml-col-email">Email</span>
            <span className="ml-col-dept">Department</span>
            <span>Balance</span>
            <span />
          </div>
        )}

        {/* Rows */}
        {employees.map(employee => {
          const initials = (
            (employee.firstName?.[0] ?? '') + (employee.lastName?.[0] ?? '')
          ).toUpperCase() || (employee.name?.[0] ?? '?').toUpperCase();

          const isOwnRow = employee.id === currentUser?.id;
          const canSeeBalance = isOwnRow || isPrivilegedUser;
          const isSelected = selected.includes(employee.id);

          let remainingLabel = null;
          let pendingLabel   = null;
          if (canSeeBalance && Object.keys(allowancesMap).length > 0) {
            try {
              const computed = calcAllowance(employee, leaveRequests, allowancesMap, schedulesMap, leaveColors);
              remainingLabel = computed.remainingLabel;
              pendingLabel   = computed.pendingLabel;
            } catch (e) {
              // silently ignore if data not yet loaded
            }
          }

          return (
            <div
              key={employee.id}
              className="ml-row ml-body"
              data-selected={isSelected}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(employee)}
              onKeyDown={e => { if (e.key === 'Enter') onSelect(employee); }}
            >
              <input
                type="checkbox"
                className="ml-check"
                checked={isSelected}
                onChange={e => { e.stopPropagation(); toggleOne(employee.id); }}
                onClick={e => e.stopPropagation()}
                aria-label={`Select ${employee.name}`}
              />

              {/* Avatar + name (+ email on small screens) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                <div style={{ flexShrink: 0, position: 'relative' }}>
                  {employee.profileImageUrl ? (
                    <img
                      src={employee.profileImageUrl}
                      alt={initials}
                      style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', display: 'block' }}
                    />
                  ) : (
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%',
                      background: employee.color ?? '#1C1A14',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 700, color: '#fff',
                    }}>
                      {initials}
                    </div>
                  )}
                  {employee.verified && (
                    <div title="Verified" style={{
                      position: 'absolute', bottom: -1, right: -1,
                      width: 11, height: 11, borderRadius: '50%',
                      background: '#16a34a', border: '2px solid var(--surface)',
                    }} />
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <p className="ml-cell" style={{ margin: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text)' }}>
                    {employee.name}
                    {isOwnRow && <span style={{ marginLeft: 6, fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}>(you)</span>}
                    {employee.status && employee.status !== 'active' && (
                      <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: 'var(--bad-text)', background: 'var(--bad-bg)', padding: '2px 8px', borderRadius: 9 }}>
                        {employee.status === 'archived' ? 'Archived' : 'Deactivated'}
                      </span>
                    )}
                  </p>
                  {employee.position && (
                    <p className="ml-cell" style={{ margin: '1px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>{employee.position}</p>
                  )}
                </div>
              </div>

              {/* Email */}
              <span className="ml-cell ml-col-email" style={{ fontSize: 13, color: 'var(--text-2)' }} title={employee.email}>
                {employee.email}
              </span>

              {/* Department */}
              <span className="ml-col-dept" style={{ minWidth: 0, display: 'flex' }}>
                {employee.dept ? (
                  <span className="ml-cell" style={{
                    fontSize: 12, fontWeight: 600, color: 'var(--accent)',
                    background: 'var(--tint)', padding: '3px 10px', borderRadius: 10, maxWidth: '100%',
                  }}>
                    {employee.dept}
                  </span>
                ) : (
                  <span style={{ fontSize: 12.5, color: 'var(--text-faint)' }}>No department</span>
                )}
              </span>

              {/* Balance — computed, not raw */}
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                {canSeeBalance && remainingLabel ? (
                  <>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', whiteSpace: 'nowrap' }}>{remainingLabel}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>left</span>
                    {pendingLabel && (
                      <span style={{
                        fontSize: 11.5, fontWeight: 600, color: 'var(--warn-text)',
                        background: 'var(--warn-bg-soft)', border: '1px solid var(--warn-border)',
                        padding: '1px 7px', borderRadius: 10, whiteSpace: 'nowrap',
                      }}>
                        {pendingLabel} pending
                      </span>
                    )}
                  </>
                ) : (
                  <span style={{ fontSize: 13, color: 'var(--field-border)' }}>—</span>
                )}
              </span>

              {/* Actions */}
              <button
                onClick={e => e.stopPropagation()}
                className="ml-more"
                aria-label={`More actions for ${employee.name}`}
              >
                <MoreHorizontal size={15} />
              </button>
            </div>
          );
        })}

        {/* Empty + inline Add user CTA */}
        {employees.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '56px 20px', gap: 10, textAlign: 'center' }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: 'var(--tint)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <UserPlus size={20} style={{ color: PRIMARY }} />
            </div>
            <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', margin: '4px 0 0' }}>No one here yet</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>Add your first teammate to start tracking leave.</p>
            <button onClick={() => setShowAddUser(true)} className="ml-add" style={{ marginTop: 6 }}>
              <UserPlus size={14} /> Add first user
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {showAddUser && (
        <AddUserModal
          departments={departments}
          currentUserName={currentUserName}
          companyName={companyName}
          onClose={() => setShowAddUser(false)}
          onCreated={(newUser) => {
            setShowAddUser(false);
            onUserCreated?.(newUser);
          }}
        />
      )}
    </>
  );
}